import React, { useEffect, useRef } from 'react';

import { LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import { TextField } from '@mui/material';

import Styles from '../lib/Styles';
import classes from './SelectDateRange.module.scss';

interface IDateRange {
  registeredAfterDate: string | undefined;
  registeredBeforeDate: string | undefined;
}

interface IDateRangeProps {
  selectedRegisteredAfterDate: Date | null;
  setSelectedRegisteredAfterDate: Function;
  selectedRegisteredBeforeDate: Date | null;
  setSelectedRegisteredBeforeDate: Function;
  dateRangeError: boolean;
  setDateRangeError: Function;
  registeredAfterDateError: boolean;
  setRegisteredAfterDateError: Function;
  registeredBeforeDateError: boolean;
  setRegisteredBeforeDateError: Function;
  readOnly: boolean;
  initialValue: IDateRange | undefined;
}

export default function SelectDateRange(props: IDateRangeProps) {
  const cx = new Styles(classes).cx;

  const { selectedRegisteredAfterDate, setSelectedRegisteredAfterDate, selectedRegisteredBeforeDate, setSelectedRegisteredBeforeDate, dateRangeError, setDateRangeError, registeredAfterDateError, setRegisteredAfterDateError, registeredBeforeDateError, setRegisteredBeforeDateError, readOnly } = props;
  let initialValue = useRef(props.initialValue);

  useEffect(() => {
    if (initialValue.current) {
      const registeredAfterDate = initialValue.current.registeredAfterDate;
      const registeredBeforeDate = initialValue.current.registeredBeforeDate;

      if (registeredAfterDate) {
        const start = new Date(registeredAfterDate);
        setSelectedRegisteredAfterDate(start);
      }

      if (registeredBeforeDate) {
        const end = new Date(registeredBeforeDate);
        setSelectedRegisteredBeforeDate(end);
      }
      initialValue.current = undefined;
    }
  }, [selectedRegisteredAfterDate, selectedRegisteredBeforeDate, setSelectedRegisteredAfterDate, setSelectedRegisteredBeforeDate])


  const handleRegisteredAfterDateChange = (registeredAfterDate: Date | null) => {
    let err: boolean = false;
    if (registeredAfterDate) {
      registeredAfterDate = new Date(registeredAfterDate.toDateString());
    }
    setSelectedRegisteredAfterDate(registeredAfterDate);
    if (registeredAfterDate) {
      try {
        registeredAfterDate.toISOString();
      } catch {
        err = true;
      }
    }
    if (!err) setRegisteredAfterDateError(false);

    if (!err) {
      let registeredBeforeDate: Date | null = selectedRegisteredBeforeDate;
      if (registeredAfterDate != null && registeredBeforeDate != null) {
        setDateRangeError(registeredBeforeDate < registeredAfterDate);
      } else {
        setDateRangeError(false);
      }
    }
  };

  const handleRegisteredBeforeDateChange = (registeredBeforeDate: Date | null) => {
    let err: boolean = false;
    if (registeredBeforeDate) {
      registeredBeforeDate = new Date(registeredBeforeDate.toDateString());
    }
    setSelectedRegisteredBeforeDate(registeredBeforeDate);
    if (registeredBeforeDate) {
      try {
        registeredBeforeDate.toISOString();
      } catch {
        err = true;
      }
    }
    if (!err) setRegisteredBeforeDateError(false);

    if (!err) {
      let registeredAfterDate: Date | null = selectedRegisteredAfterDate;
      if (registeredAfterDate != null && registeredBeforeDate != null) {
        setDateRangeError(registeredBeforeDate < registeredAfterDate);
      } else {
        setDateRangeError(false);
      }
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    let err = false;
    if (selectedRegisteredAfterDate) {
      try {
        selectedRegisteredAfterDate?.toISOString();
      } catch {
        err = true;
      }
    }
    setRegisteredAfterDateError(err);

    err = false;
    if (selectedRegisteredBeforeDate) {
      try {
        selectedRegisteredBeforeDate?.toISOString();
      } catch {
        err = true;
      }
    }
    setRegisteredBeforeDateError(err);
  }

  return (
    <div className={cx('date-range')}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div className={cx('date-picker-container')}>
          <DesktopDatePicker
            disabled={readOnly}
            label="Start Date"
            value={selectedRegisteredAfterDate}
            onChange={(date) => handleRegisteredAfterDateChange(date)}
            renderInput={(props) => (
              <TextField
                {...props}
                error={Boolean(dateRangeError || registeredAfterDateError)}
                helperText={registeredAfterDateError ? 'Invalid date' : dateRangeError ? 'Start date is after end date.' : ''}
                onBlur={handleBlur}
              />
            )}
          />
          <DesktopDatePicker
            disabled={readOnly}
            label="End Date"
            value={selectedRegisteredBeforeDate}
            onChange={(date) => handleRegisteredBeforeDateChange(date)}
            renderInput={(props) => (
              <TextField
                {...props}
                error={Boolean(dateRangeError || registeredBeforeDateError)}
                helperText={registeredBeforeDateError ? 'Invalid date' : dateRangeError ? 'End date is before start date.' : ''}
                onBlur={handleBlur}
              />
            )}
          />
        </div>
      </LocalizationProvider>
    </div>

  );
}